// Right-rail trip summary used on passengers / reprice / payment pages.
// Markup mirrors 06-passengers.html .summary-card with timer-strip,
// trip-leg(s) and fare-list.

import { useMemo } from "react";
import { HoldTimer } from "./HoldTimer";
import { fmtCabin } from "../lib/labels";
import type { Itinerary } from "../lib/types";

export function TripSummaryCard({
  booking,
  ctaLabel,
  onCta,
  hideCta = false,
  ctaDisabled = false,
  ctaLoading = false,
  helpText,
}: {
  booking: Itinerary;
  ctaLabel?: string;
  onCta?: () => void;
  hideCta?: boolean;
  ctaDisabled?: boolean;
  ctaLoading?: boolean;
  helpText?: React.ReactNode;
}) {
  const item = booking.itinerary_items[0];
  const pricing = booking.pricing;
  const paxCount = (booking.pax_count?.adult ?? 0) + (booking.pax_count?.child ?? 0) + (booking.pax_count?.infant ?? 0);
  const paxLabel = `${paxCount} ${paxCount === 1 ? "adult" : "travellers"}`;
  const refundable = item?.is_refundable;
  const cabin = fmtCabin(item?.legs[0]?.segments[0]?.cabin_class);
  // Resolve the booking hold target once so the countdown is stable across
  // rerenders (otherwise it'd jitter as `remaining_seconds` recomputed).
  const targetMs = useMemo(() => {
    if (booking.expires_at) {
      const t = new Date(booking.expires_at).getTime();
      if (!Number.isNaN(t)) return t;
    }
    if (booking.remaining_seconds != null) return Date.now() + booking.remaining_seconds * 1000;
    return null;
    // booking.id changes per booking; the fields above are derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.booking_id]);

  return (
    <aside className="booking-sidebar">
      <div className="summary-card">
        {targetMs != null && (
          <HoldTimer targetMs={targetMs} prefix="Booking holds for" />
        )}

        <h3>Trip <span className="serif">summary</span></h3>
        <div className="sub">
          {summaryRoute(booking)} · {paxLabel} · {refundable ? "Refundable" : "Non-refundable"} {cabin.toLowerCase()}
        </div>

        {item?.legs.map((leg, li) => {
          const first = leg.segments[0];
          const last = leg.segments[leg.segments.length - 1];
          return (
            <div className="trip-leg" key={li}>
              <div className="dir">{capitalize(leg.direction)} · {fmtDateLong(first?.origin.departure_time)}</div>
              <div className="leg-route">{first.origin.airport_code} → {last.destination.airport_code}</div>
              <div className="leg-meta">
                {first.airline.code} {first.airline.flight_number} · {fmtTime(first.origin.departure_time)} → {fmtTime(last.destination.arrival_time)} · {leg.stop_count === 0 ? "direct" : `${leg.stop_count} stop`}
              </div>
            </div>
          );
        })}

        <div className="fare-list">
          <div className="fare-line"><span className="lbl">Base fare · {paxLabel}</span><span>{fmtMoney(pricing.base_fare, pricing.currency)}</span></div>
          {pricing.tax_and_surcharge != null && (
            <div className="fare-line"><span className="lbl">Taxes &amp; surcharge</span><span>{fmtMoney(pricing.tax_and_surcharge, pricing.currency)}</span></div>
          )}
          <div className="fare-line"><span className="lbl">Service fee</span><span>{fmtMoney(pricing.service_fee ?? 0, pricing.currency)}</span></div>
          <div className="fare-line total"><span className="lbl">You'll pay</span><span className="amt">{fmtMoney(pricing.final_fare, pricing.currency)}</span></div>
        </div>

        {!hideCta && (
          <button
            type="button"
            className={`btn btn-yellow btn-pill btn-full${ctaLoading ? " loading" : ""}`}
            style={{ marginTop: 18 }}
            disabled={ctaDisabled || ctaLoading}
            onClick={onCta}
          >
            {ctaLabel || "Save & continue"}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
          </button>
        )}

        {helpText && (
          <p className="field-help" style={{ marginTop: 12, textAlign: "center" }}>{helpText}</p>
        )}
      </div>
    </aside>
  );
}

function summaryRoute(b: Itinerary): string {
  const item = b.itinerary_items[0];
  if (!item) return "";
  const firstLeg = item.legs[0];
  const lastLeg = item.legs[item.legs.length - 1];
  const origin = firstLeg.segments[0].origin.airport_code;
  const destination = firstLeg.segments[firstLeg.segments.length - 1].destination.airport_code;
  if (item.legs.length > 1 && lastLeg.segments[lastLeg.segments.length - 1].destination.airport_code === origin) {
    return `${origin} ⇄ ${destination}`;
  }
  return `${origin} → ${destination}`;
}

function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDateLong(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
function fmtMoney(n: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString("en-IN")}`;
  }
}
function capitalize(s: string): string { return s ? s[0].toUpperCase() + s.slice(1) : s; }
